import { cloneElement, ReactElement } from "react";
import { Link, Tooltip } from "@mui/material";
import Rows from "../../../library/components/layout/Rows";
import Columns from "../../../library/components/layout/Columns";
import { appIcons } from "../../../app/appIcons";
import appColors from "../../../app/appColors";
import DateTimeToStringHelper from "../../../library/helpers/DateTimeToStringHelper";
import StringHelper from "../../../library/helpers/StringHelper";
import { HesapSarkiSesKayitNotlarModel } from "../../../models/businessModels/HesapSarkiSesKayitNotlarModel";
import { getHataNotDotTooltip } from "./pdfCanvasNotHelpers";
import { getPdfCanvasPanelBaslikStyle, getPdfCanvasPanelGrupStyle } from "./pdfCanvasPanelStyles";
import type { PdfCanvasColorMeaning } from "./pdfCanvasTypes";

interface IPdfHataNotlarPanelProps {
    isWideLayout: boolean,
    readOnly: boolean,
    notlar: HesapSarkiSesKayitNotlarModel[],
    notlarSureyeGore: HesapSarkiSesKayitNotlarModel[],
    legendItems: PdfCanvasColorMeaning[],
    onDinleNot: (not: HesapSarkiSesKayitNotlarModel) => void,
    onGuncelleNotYorumlar: (not: HesapSarkiSesKayitNotlarModel) => void,
    onSilNot: (not: HesapSarkiSesKayitNotlarModel) => void,
}

const PdfHataNotlarPanel = (props: IPdfHataNotlarPanelProps) => {
    const panelBaslikStyle = getPdfCanvasPanelBaslikStyle(props.isWideLayout);
    const panelGrupStyle = getPdfCanvasPanelGrupStyle();
    const hataNotSatirFontSize = props.isWideLayout ? "0.72rem" : "0.82rem";

    const hatalarNotlarCountString = props.notlar.length === 0 ? "" : `(${props.notlar.length.toFixed()})`;

    return <Rows gap={4} p={8} style={panelGrupStyle}>
        <span style={panelBaslikStyle}>Hatalar/Notlar {hatalarNotlarCountString}</span>
        <Rows
            gap={4}
            style={{
                flexShrink: 1,
                maxHeight: props.isWideLayout ? 180 : 140,
                overflowY: "auto",
                overflowX: "hidden",
                minHeight: 0,
            }}>
            {props.notlar.length === 0
                ? <span style={{ fontSize: hataNotSatirFontSize, color: appColors.TEXT_SECONDARY, lineHeight: 1.2 }}>hata/not bulunmamaktadır</span>
                : props.notlarSureyeGore.map(not =>
                    <Columns key={not.id} gap={6} style={{ alignItems: "center", flexShrink: 0, lineHeight: 1.2 }}>
                        <Tooltip title={getHataNotDotTooltip(not, props.legendItems)} arrow>
                            <span style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                border: "1px solid #999",
                                background: not.renkKod ?? appColors.GRAY_LIGHT2,
                                flexShrink: 0,
                                cursor: "default",
                            }} />
                        </Tooltip>
                        <Columns gap={12} style={{ alignItems: "center", flexWrap: "nowrap", minWidth: 0 }}>
                            <span style={{ fontSize: hataNotSatirFontSize, lineHeight: 1.2 }}>
                                {not.sayfaNo}. sayfa, {DateTimeToStringHelper.convertSecondsToDurationString(not.sure ?? 0)}
                            </span>
                            {!props.readOnly
                                ? <Link
                                    component="button"
                                    variant="body2"
                                    underline="hover"
                                    title="Yorumları düzenle"
                                    sx={{
                                        p: 0,
                                        m: 0,
                                        minWidth: 0,
                                        minHeight: 0,
                                        lineHeight: 1.2,
                                        fontSize: hataNotSatirFontSize,
                                        flexShrink: 0,
                                        color: appColors.TEXT_SECONDARY,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        "& .MuiSvgIcon-root": { fontSize: 14 },
                                    }}
                                    onClick={() => props.onGuncelleNotYorumlar(not)}>
                                    {cloneElement(appIcons.Genel.yorumMini as ReactElement, {
                                        color: StringHelper.isNotEmpty(not.yorumlar) ? "primary" : "disabled",
                                        sx: { fontSize: 14 },
                                    })}
                                </Link>
                                : null}
                            <Link
                                component="button"
                                variant="body2"
                                underline="hover"
                                sx={{
                                    p: 0,
                                    m: 0,
                                    minWidth: 0,
                                    minHeight: 0,
                                    lineHeight: 1.2,
                                    fontSize: hataNotSatirFontSize,
                                    flexShrink: 0,
                                    color: appColors.GREEN_MAIN,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "2px",
                                    verticalAlign: "middle",
                                    "& .MuiSvgIcon-root": { fontSize: 14 },
                                }}
                                onClick={() => props.onDinleNot(not)}>
                                {cloneElement(appIcons.Sarki.oynatMini as ReactElement, { color: "success", sx: { fontSize: 14 } })}
                                Dinle
                            </Link>
                            {!props.readOnly
                                ? <Link
                                    component="button"
                                    variant="body2"
                                    underline="none"
                                    title="Notu sil"
                                    sx={{
                                        p: 0,
                                        m: 0,
                                        minWidth: 0,
                                        minHeight: 0,
                                        lineHeight: 1.2,
                                        flexShrink: 0,
                                        color: appColors.RED_MAIN,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        "& .MuiSvgIcon-root": { fontSize: 14 },
                                    }}
                                    onClick={() => props.onSilNot(not)}>
                                    {cloneElement(appIcons.CrudIcons.sil as ReactElement, { color: "error", sx: { fontSize: 14 } })}
                                </Link>
                                : null}
                        </Columns>
                    </Columns>
                )}
        </Rows>
    </Rows>;
};

export default PdfHataNotlarPanel;
